/**
 * AI Context Builder
 *
 * Builds the system prompt for AI interactions
 * Includes context from saved items to enable personalized responses
 */

import type { Item, OpenRouterMessage } from '@/types';

const MAX_CONTEXT_ITEMS = 30;

/**
 * Build the system prompt with context from user's items
 *
 * @param items - All items from the user's collection
 * @returns System prompt string
 */
export function buildSystemPrompt(items: Item[]): string {
  // Sort by most recent, take top N
  const sorted = [...items].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  const recent = sorted.slice(0, MAX_CONTEXT_ITEMS);

  // Build context lines - natural format for better AI responses
  const contextLines = recent.map((item) => {
    const tags = item.tags.length ? ` (tagged: ${item.tags.join(', ')})` : '';
    const summary = getItemSummary(item);
    return `${item.type}: ${item.title}${tags} - ${summary}`;
  });

  return `You are a friendly, helpful assistant for a personal knowledge management app called Personal Insights.

The user has ${items.length} saved items. Here are their ${recent.length} most recent:
${contextLines.join('\n')}

How to respond:
- Be conversational and natural - don't use bullet points or technical formatting unless asked
- Refer to items casually by name, like "your task about reading the Selina article"
- Keep responses concise and friendly
- If you can't find something, just say so naturally
- Help find connections between their saved content when relevant`;
}

/**
 * Get a short summary for an item based on its type
 */
function getItemSummary(item: Item): string {
  switch (item.type) {
    case 'link':
      return item.description || item.url;
    case 'podcast':
      return item.podcastName
        ? `${item.podcastName} - ${item.description || item.url}`
        : item.description || item.url;
    case 'note':
    case 'idea':
      return (
        item.plainTextPreview.slice(0, 150) + (item.plainTextPreview.length > 150 ? '...' : '')
      );
    case 'file':
      return `${item.originalFileName} (${formatBytes(item.fileSize)})`;
    case 'task':
      return item.isCompleted ? 'completed' : 'not done yet';
  }
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Build the full message array for an AI request
 *
 * @param items - User's items for context
 * @param chatHistory - Previous messages in the conversation
 * @param userMessage - Current user message
 * @returns Array of messages ready for OpenRouter API
 */
export function buildAIMessages(
  items: Item[],
  chatHistory: OpenRouterMessage[],
  userMessage: string
): OpenRouterMessage[] {
  const systemPrompt = buildSystemPrompt(items);

  return [
    { role: 'system', content: systemPrompt },
    ...chatHistory,
    { role: 'user', content: userMessage },
  ];
}

/**
 * Create a message asking about a specific item
 *
 * @param item - Item to ask about
 * @returns Pre-filled user message
 */
export function createItemQuery(item: Item): string {
  switch (item.type) {
    case 'link':
      return `Tell me more about this link: "${item.title}" (${item.url})`;
    case 'podcast':
      return `What can you tell me about this podcast: "${item.title}"?`;
    case 'note':
    case 'idea':
      return `I have a ${item.type} titled "${item.title}". Can you help me expand on it?`;
    case 'file':
      return `I have a file called "${item.originalFileName}". What might be useful to know about it?`;
    case 'task':
      return `I have a task: "${item.title}". Can you help me break it down or suggest how to approach it?`;
  }
}

/**
 * Suggest related items based on a query
 *
 * @param items - All items to search through
 * @param query - Search query
 * @param limit - Maximum items to return
 * @returns Suggested items
 */
export function findRelatedItems(items: Item[], query: string, limit = 5): Item[] {
  const queryLower = query.toLowerCase();
  const words = queryLower.split(/\s+/).filter((w) => w.length > 2);

  // Score items based on query match
  const scored = items.map((item) => {
    let score = 0;
    const searchText = `${item.title} ${item.description} ${item.tags.join(' ')}`.toLowerCase();

    // Exact phrase match
    if (searchText.includes(queryLower)) {
      score += 10;
    }

    // Individual word matches
    for (const word of words) {
      if (searchText.includes(word)) {
        score += 1;
      }
    }

    // Tag matches
    for (const tag of item.tags) {
      if (queryLower.includes(tag.toLowerCase())) {
        score += 3;
      }
    }

    return { item, score };
  });

  // Filter and sort by score
  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.item);
}
